import { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import { createRoot } from "react-dom/client";
import type { Map as MapboxMap } from "mapbox-gl";
import type {
  GeoJsonFeatureCollection,
  GeoJsonFeature,
} from "@typings/components/map";
import type { MarkerConfig } from "@typings/components/map";
import type { ElementType } from "react";

type UseMapMarkerPopupProps<T = Record<string, string>> = {
  map: MapboxMap | null;
  geoJsonData: GeoJsonFeatureCollection<T> | null;
  markerConfig?: MarkerConfig;
  popupComponent?: ElementType<{
    feature: GeoJsonFeature<T>;
    onNavigate: (path: string) => void;
  }>;
  navigate: (path: string) => void;
};

function useMapMarkerPopup<T = Record<string, string>>({
  map,
  geoJsonData,
  popupComponent,
  navigate,
}: UseMapMarkerPopupProps<T>) {
  const geoJsonDataRef = useRef(geoJsonData);

  useEffect(() => {
    geoJsonDataRef.current = geoJsonData;
  }, [geoJsonData]);

  useEffect(() => {
    if (!map) return;

    const sourceId = "custom-points";
    const layerId = "custom-points-layer";
    let currentPopup: mapboxgl.Popup | null = null;

    const setupLayers = () => {
      const currentGeoJson = geoJsonDataRef.current;
      if (!map.isStyleLoaded()) return;

      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);

      map.addSource(sourceId, {
        type: "geojson",
        data: currentGeoJson || { type: "FeatureCollection", features: [] },
      });

      map.addLayer({
        id: layerId,
        type: "circle",
        source: sourceId,
        paint: {
          "circle-radius": 6,
          "circle-color": ["get", "color"],
          "circle-stroke-width": 2,
          "circle-stroke-color": "#FFFFFF",
        },
      });
    };

    const handleInteraction = (
      e: mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent,
    ) => {
      e.preventDefault();

      const feature = e.features?.[0] as GeoJsonFeature<T> | undefined;
      if (!feature || !popupComponent) return;

      const popupElement = document.createElement("div");
      const root = createRoot(popupElement);
      const PopupComponent = popupComponent;
      root.render(<PopupComponent feature={feature} onNavigate={navigate} />);

      if (currentPopup) {
        currentPopup.remove();
        currentPopup = null;
      }

      const popup = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: false,
        maxWidth: "300px",
        offset: 15,
        anchor: "bottom",
      })
        .setLngLat(feature.geometry.coordinates)
        .setDOMContent(popupElement)
        .addTo(map);

      currentPopup = popup;

      popup.on("close", () => {
        setTimeout(() => root.unmount(), 100);
        currentPopup = null;
      });
    };

    const handleStyleData = () => {
      setupLayers();
    };

    if (geoJsonDataRef.current) {
      setupLayers();
    }

    map.on("styledata", handleStyleData);
    map.on("style.load", handleStyleData);

    map.on("click", layerId, handleInteraction);
    map.on("touchend", layerId, handleInteraction);
    map.on("mouseenter", layerId, () => {
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", layerId, () => {
      map.getCanvas().style.cursor = "";
    });

    return () => {
      map.off("styledata", handleStyleData);
      map.off("style.load", handleStyleData);
      map.off("click", layerId, handleInteraction);
      map.off("touchend", layerId, handleInteraction);
      map.off("mouseenter", layerId, () => {});
      map.off("mouseleave", layerId, () => {});

      if (currentPopup) {
        currentPopup.remove();
      }

      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    };
  }, [map, navigate, popupComponent]);

  useEffect(() => {
    if (map && map.isStyleLoaded() && map.getSource("custom-points")) {
      (map.getSource("custom-points") as mapboxgl.GeoJSONSource).setData(
        geoJsonData || { type: "FeatureCollection", features: [] },
      );
    }
  }, [geoJsonData, map]);
}

export default useMapMarkerPopup;
